import React, { createContext, useContext, useEffect, useState } from 'react';
import { components } from 'apis';
import { useApi } from '../query/QueryProvider';
import { useAuth } from 'react-oidc-context';
import { UseQueryResult } from '@tanstack/react-query';
import Cookies from 'universal-cookie';
type ChannelInfo = components['schemas']['GetChannelInfoResponse'];
type UserSettings = components['schemas']['UserSettings'];
type UserNotificationsSettings =
    components['schemas']['UserNotificationsSettings'];

type UserContextType = {
    channel?: ChannelInfo;
    settings?: UserSettings;
    updateUserSettings: (newSettings: Partial<UserSettings>) => void;
    userNotificationsSettings?: UserNotificationsSettings;
    updateUserNotificationSettings: (
        newSettings: Partial<UserNotificationsSettings>,
    ) => void;
};

export const UserContext = createContext<UserContextType | undefined>(
    undefined,
);

export const UserProvider: React.FC<React.PropsWithChildren<{}>> = (props) => {
    const { api } = useApi();
    const { isAuthenticated, user, events, signinSilent } = useAuth();
    const [channel, setChannel] = useState<ChannelInfo | undefined>(undefined);
    const [settings, setSettings] = useState<UserSettings>({
        contactMe: false,
        privatePlaylists: false,
        privateSubscriptions: false,
        language: 'EN',
        rememberSearch: false,
        recentPlayed: false,
        chaosMode: false,
        highContrast: false,
        darkMode: false,
    });
    const [userNotificationsSettings, setUserNotificationSettings] =
        useState<UserNotificationsSettings>({
            username: '',
            subscriptions: false,
            recommended: false,
            activity: false,
            replies: false,
            mentions: false,
            sharedContent: false,
            general: false,
            creator: false,
            emailLanguage: 'EN',
        });
    const getChannelInfo: UseQueryResult<ChannelInfo> = api.useQuery(
        'get',
        '/channel/info',
        {},
        { enabled: false },
    );
    const getUserSettings: UseQueryResult<UserSettings> = api.useQuery(
        'get',
        '/user/settings',
        {},
        { enabled: false },
    );
    const getUserNotificationsSettings: UseQueryResult<UserNotificationsSettings> =
        api.useQuery(
            'get',
            '/user/settings/notifications',
            {},
            { enabled: false },
        );
    const updateUserSettingsApi = api.useMutation('patch', '/user/settings');
    const updateUserSettings = (newSettings: Partial<UserSettings>) => {
        updateUserSettingsApi.mutateAsync({ body: newSettings }).then(() => {
            setSettings({ ...settings, ...newSettings });
        });
    };
    const updateUserNotificationSettingsApi = api.useMutation(
        'patch',
        '/user/settings/notifications',
    );
    const updateUserNotificationSettings = (
        newSettings: Partial<UserNotificationsSettings>,
    ) => {
        updateUserNotificationSettingsApi
            .mutateAsync({ body: newSettings })
            .then(() => {
                setUserNotificationSettings({
                    ...userNotificationsSettings,
                    ...newSettings,
                });
            });
    };
    useEffect(() => {
        // the `return` is important - addAccessTokenExpiring() returns a cleanup function
        return events.addAccessTokenExpiring(() => {
            signinSilent().then((user) => {
                const cookies = new Cookies();
                cookies.set('access_token', user?.access_token, {
                    path: '/',
                    domain: 'bluechip.link',
                });
            });
        });
    }, [events, signinSilent]);
    useEffect(() => {
        signinSilent();
    }, []);
    useEffect(() => {
        if (isAuthenticated) {
            console.log('AM AUTHENTICATED');
            console.log(user);
            const cookies = new Cookies();
            cookies.set('access_token', user?.access_token, {
                path: '/',
                domain: 'bluechip.link',
            });
            getChannelInfo.refetch().then((res) => {
                if (res.data) {
                    console.log(res.data);
                    setChannel(res.data);
                }
            });
            getUserSettings.refetch().then((res) => {
                if (res.data) {
                    setSettings(res.data);
                }
            });
            getUserNotificationsSettings.refetch().then((res) => {
                if (res.data) {
                    setUserNotificationSettings(res.data);
                }
            });
        }
    }, [isAuthenticated]);

    return (
        <UserContext.Provider
            value={{
                channel,
                settings,
                updateUserSettings,
                userNotificationsSettings,
                updateUserNotificationSettings,
            }}
        >
            {props.children}
        </UserContext.Provider>
    );
};

export function useUserContext() {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUserContext must be within UserProvider');
    }
    return context;
}
